'use client';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SCORING_RULES } from '@/lib/constants/scoring';
import { isLeaderboardEligible } from '@/features/typing-engine';

/**
 * Post-test result screen.
 * @param {{
 *   metrics: object,
 *   durationSeconds: number,
 *   personalBest: object | null,
 *   onRetry: () => void,
 *   onNewTest: () => void,
 * }} props
 */
export function ResultScreen({ metrics, durationSeconds, personalBest, onRetry, onNewTest }) {
  const eligible = isLeaderboardEligible({
    durationSeconds: Math.max(1, Math.round(metrics.elapsedSeconds)),
    netWpm: metrics.netWpm,
    accuracy: metrics.accuracy,
    correctChars: metrics.correctChars,
  });

  const beatsBest = personalBest && metrics.netWpm > personalBest.netWpm;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary">Test complete</h2>
        <p className="mt-1 text-sm text-text-secondary">
          {durationSeconds}-second typing test
        </p>
      </div>

      <Card>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          <div>
            <div className="text-3xl font-bold tabular-nums text-text-primary">
              {Math.round(metrics.netWpm)}
            </div>
            <div className="text-xs text-text-muted">Net WPM</div>
          </div>
          <div>
            <div className="text-3xl font-bold tabular-nums text-text-secondary">
              {Math.round(metrics.grossWpm)}
            </div>
            <div className="text-xs text-text-muted">Gross WPM</div>
          </div>
          <div>
            <div className="text-3xl font-bold tabular-nums text-success">
              {Math.round(metrics.accuracy)}%
            </div>
            <div className="text-xs text-text-muted">Accuracy</div>
          </div>
          <div>
            <div className="text-3xl font-bold tabular-nums text-text-primary">
              {metrics.correctChars}
            </div>
            <div className="text-xs text-text-muted">Correct chars</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm sm:grid-cols-4">
          <div>
            <span className="text-text-muted">Incorrect</span>
            <div className="font-medium tabular-nums text-error">{metrics.incorrectChars}</div>
          </div>
          <div>
            <span className="text-text-muted">Backspaces</span>
            <div className="font-medium tabular-nums">{metrics.backspaces}</div>
          </div>
          <div>
            <span className="text-text-muted">Elapsed</span>
            <div className="font-medium tabular-nums">{Math.round(metrics.elapsedSeconds)}s</div>
          </div>
          <div>
            <span className="text-text-muted">Leaderboard</span>
            <div className={`font-medium ${eligible ? 'text-success' : 'text-text-muted'}`}>
              {eligible ? 'Eligible' : 'Not eligible'}
            </div>
          </div>
        </div>
      </Card>

      {personalBest && (
        <div className="rounded-lg border border-border bg-surface-raised p-4 text-sm">
          <span className="text-text-muted">Previous personal best ({durationSeconds}s): </span>
          <span className="font-semibold tabular-nums text-text-primary">
            {Math.round(personalBest.netWpm)} WPM
          </span>
          {beatsBest && (
            <span className="ml-2 rounded-full bg-achievement-surface px-2 py-0.5 text-xs font-medium text-achievement">
              New personal best
            </span>
          )}
        </div>
      )}

      <details className="rounded-lg border border-border bg-surface-raised p-4 text-sm">
        <summary className="cursor-pointer font-medium text-text-secondary">
          How these numbers are calculated
        </summary>
        <dl className="mt-3 space-y-2 text-text-muted">
          <div>
            <dt className="font-medium text-text-secondary">Gross WPM</dt>
            <dd>{SCORING_RULES.grossWpm}</dd>
          </div>
          <div>
            <dt className="font-medium text-text-secondary">Net WPM</dt>
            <dd>{SCORING_RULES.netWpm}</dd>
          </div>
          <div>
            <dt className="font-medium text-text-secondary">Accuracy</dt>
            <dd>{SCORING_RULES.accuracy}</dd>
          </div>
        </dl>
      </details>

      <div className="flex flex-wrap gap-3">
        <Button onClick={onRetry} size="lg">Try again</Button>
        <Button onClick={onNewTest} variant="secondary" size="lg">New test</Button>
      </div>
    </div>
  );
}